import { Box, Typography, Card, CardContent, Stack } from "@mui/material";
import {
  FormattedManagementsList,
  ManagementsDetailHeader,
} from "../../../core/models/managements/managements.model";
import "./ManagementsLast.scss";
import { useTranslation } from "react-i18next";
import { formatDate } from "../../../utils/dateUtils/formatDate.util";
import {
  getManagementStateData,
  getManagementTitleText,
  getManagementCodeTextIcon,
  getIncidenceIconTitle,
  incedenceTexts,
} from "../../../utils/components/managements/managements.util";
import theme from "../../../muiTheme";
import LoadingComponentComponent from "../../UI/LoadingComponentComponent/LoadingComponentComponent";
import { MANAGEMENTS_REGISTER_TYPE_CLAIMS } from "../../../core/constants/constants";
import { useNavigate } from "react-router-dom";

interface ManagementsLastProps {
  managementsLastList: FormattedManagementsList[];
  isTableLoading: boolean;
  setManagementsDetailHeader: React.Dispatch<
    React.SetStateAction<ManagementsDetailHeader>
  >;
}
const ManagementsLast = ({
  managementsLastList,
  isTableLoading,
  setManagementsDetailHeader,
}: ManagementsLastProps) => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  return (
    <Box component="section" sx={{ paddingTop: { xs: "2.5rem", md: "5rem" } }}>
      <Typography component="h3" variant="h3" paddingBottom="1rem">
        {t("Gestiones.Recientes")}
      </Typography>
      <Typography
        component="p"
        variant="body2"
        sx={{ color: "grey.700" }}
        paddingBottom={{ xs: "1.5rem", md: "2rem" }}
      >
        {!managementsLastList.length && !isTableLoading
          ? t("Gestiones.No hay gestiones recientes")
          : t("Gestiones.Descripción Recientes")}
      </Typography>
      {isTableLoading && (
        <Box padding={"2rem 0"}>
          <LoadingComponentComponent
            isLoading={isTableLoading}
            backgroundColor={"transparent"}
            position={"relative"}
            height={"4rem"}
          />
        </Box>
      )}
      {managementsLastList.length > 0 &&
        managementsLastList.map(
          ({
            issueNumber,
            date,
            state,
            recordTextType,
            claimTextSubType,
            incidenceText,
          }) => {
            const { color, backgroundColor, icon, label } =
              getManagementStateData(state);
            const isIncidenceText = incedenceTexts.some(
              (incidenceMatchingText) =>
                incidenceText.includes(incidenceMatchingText)
            );
            const incidenceIconTitle = getIncidenceIconTitle(
              incedenceTexts,
              incidenceText
            );

            return (
              <Card
                className="last-managements__card"
                sx={{
                  borderRadius: "12px",
                  marginBottom: "1rem",
                  cursor: "pointer",
                }}
                key={issueNumber}
                onClick={() => {
                  navigate(
                    `${process.env.REACT_APP_MANAGEMENT_DETAIL}/${issueNumber}`
                  );
                  setManagementsDetailHeader({
                    recordTextType,
                    claimTextSubType,
                    incidenceText,
                  });
                }}
              >
                <CardContent sx={{ "&&": { padding: "16px" } }}>
                  <Stack justifyContent="space-between" direction="row">
                    <Stack direction="row" gap="2rem" sx={{ flexBasis: "40%" }}>
                      <Typography
                        fontSize={"32px"}
                        component="p"
                        color="primary.main"
                        margin={{ xs: "0", md: "auto 0" }}
                        style={{ width: "32px" }}
                      >
                        <span
                          className={
                            recordTextType === MANAGEMENTS_REGISTER_TYPE_CLAIMS
                              ? "t-icon-warning"
                              : isIncidenceText
                              ? incidenceIconTitle.icon
                              : getManagementCodeTextIcon(claimTextSubType).icon
                          }
                        />
                      </Typography>
                      <Stack
                        direction={{ xs: "column", md: "row" }}
                        justifyContent={"flex-start"}
                        width="100%"
                        gap={{ xs: "0.5rem", md: "2rem" }}
                      >
                        <Stack direction="column" sx={{ flexBasis: { md: "300px" } }}>
                          <Typography
                            component="h6"
                            variant="body2"
                            fontWeight={400}
                          >
                            {t(
                              `Gestiones.${
                                isIncidenceText
                                  ? incidenceIconTitle.title
                                  : getManagementTitleText(
                                      recordTextType,
                                      claimTextSubType
                                    )
                              }`
                            )}
                          </Typography>
                          <Typography variant="caption">
                            {`${t("Gestiones.Solicitado el")} ${formatDate(
                              new Date(date)
                            )}`}
                          </Typography>
                        </Stack>
                        <Typography
                          variant="body2"
                          component="span"
                          width={"fit-content"}
                          sx={{
                            backgroundColor,
                            color,
                          }}
                          className="state-chip"
                          height={"fit-content"}
                          alignSelf={{ xs: "start", md: "center" }}
                        >
                          <span aria-hidden="true" className={icon} />
                          {t(label)}
                        </Typography>
                      </Stack>
                    </Stack>
                    <Typography
                      component="h5"
                      variant="h5"
                      color={theme.palette.primary.main}
                      margin="auto 0"
                    >
                      <span
                        className="t-icon-long-arrow-right"
                        style={{ alignSelf: "flex-end" }}
                      />
                    </Typography>
                  </Stack>
                </CardContent>
              </Card>
            );
          }
        )}
    </Box>
  );
};

export default ManagementsLast;
